import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  User, 
  Mail, 
  Building, 
  Briefcase, 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Plus,
  ArrowUpDown
} from 'lucide-react';
import NavbarComptable from '../components/NavbarComptable';
import SidebarComptable from '../components/SidebarComptable';

const ListeClients = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [notification, setNotification] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [sortBy, setSortBy] = useState("nom");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    filterAndSortClients();
  }, [clients, searchTerm, filterStatus, sortBy, sortOrder]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://api-ec.cinef-expertises.com/api/clients");
      setClients(response.data);
    } catch (error) {
      showNotification("Erreur lors du chargement des clients", "error");
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortClients = () => {
    let filtered = [...clients];

    // Filtrer par recherche
    if (searchTerm) {
      filtered = filtered.filter(client =>
        client.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.entreprise?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrer par statut
    if (filterStatus !== "all") {
      filtered = filtered.filter(client => 
        filterStatus === "active" ? client.is_active : !client.is_active
      );
    }

    // Trier
    filtered.sort((a, b) => {
      let aVal = a[sortBy] || "";
      let bVal = b[sortBy] || "";
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredClients(filtered);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const toggleClientStatus = async (clientId, currentStatus) => {
    try {
      await axios.patch(`https://api-ec.cinef-expertises.com/api/clients/${clientId}/toggle-status`, {
        is_active: !currentStatus
      });
      
      setClients(clients.map(client => 
        client.id === clientId ? { ...client, is_active: !currentStatus } : client
      ));
      
      showNotification(
        `Client ${!currentStatus ? 'activé' : 'désactivé'} avec succès`,
        'success'
      );
      setShowActionMenu(null);
    } catch (error) {
      showNotification("Erreur lors de la modification du statut", "error");
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce client ?")) {
      return;
    }

    try {
      await axios.delete(`https://api-ec.cinef-expertises.com/api/clients/${clientId}`);
      setClients(clients.filter(client => client.id !== clientId));
      showNotification("Client supprimé avec succès", "success");
      setShowActionMenu(null);
    } catch (error) {
      showNotification("Erreur lors de la suppression", "error");
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  return (
    <>
      <div className="app-container">
        <SidebarComptable
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <div className="main-wrapper">
          <NavbarComptable
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
          />

          <main className="main-content">
            {/* Notification */}
            {notification && (
              <div className={`notification ${notification.type}`}>
                {notification.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                <span>{notification.message}</span>
              </div>
            )}

            {/* Header Section */}
            <div className="page-header">
              <div className="header-content">
                <div className="header-text">
                  <h1 className="page-title">Liste des clients</h1>
                  <p className="page-subtitle">
                    Gérez vos clients, consultez leurs informations et modifiez leur statut
                  </p>
                </div>
                <button 
                  className="btn-add"
                  onClick={() => navigate("/dashboard-comptable/create-client")}
                >
                  <Plus size={20} />
                  <span>Nouveau client</span>
                </button>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="filters-section">
              <div className="search-box">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Rechercher un client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <Filter size={18} />
                <select 
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">Tous les clients</option>
                  <option value="active">Actifs</option>
                  <option value="inactive">Inactifs</option>
                </select>
              </div>

              <div className="stats-summary">
                <div className="stat-item">
                  <span className="stat-label">Total:</span>
                  <span className="stat-value">{clients.length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Actifs:</span>
                  <span className="stat-value active">{clients.filter(c => c.is_active).length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Inactifs:</span>
                  <span className="stat-value inactive">{clients.filter(c => !c.is_active).length}</span>
                </div>
              </div>
            </div>

            {/* Clients Table */}
            <div className="table-container">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner-large"></div>
                  <p>Chargement des clients...</p>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="empty-state">
                  <User size={48} />
                  <h3>Aucun client trouvé</h3>
                  <p>
                    {searchTerm || filterStatus !== "all" 
                      ? "Aucun client ne correspond à vos critères de recherche"
                      : "Commencez par créer votre premier client"
                    }
                  </p>
                  {!searchTerm && filterStatus === "all" && (
                    <button 
                      className="btn-add"
                      onClick={() => navigate("/dashboard-comptable/create-client")}
                    >
                      <Plus size={20} />
                      <span>Créer un client</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="clients-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('nom')}>
                          <div className="th-content">
                            <User size={16} />
                            <span>Client</span>
                            <ArrowUpDown size={14} className={sortBy === 'nom' ? 'active' : ''} />
                          </div>
                        </th>
                        <th onClick={() => handleSort('email')}>
                          <div className="th-content">
                            <Mail size={16} />
                            <span>Email</span>
                            <ArrowUpDown size={14} className={sortBy === 'email' ? 'active' : ''} />
                          </div>
                        </th>
                        <th onClick={() => handleSort('entreprise')}>
                          <div className="th-content">
                            <Building size={16} />
                            <span>Entreprise</span>
                            <ArrowUpDown size={14} className={sortBy === 'entreprise' ? 'active' : ''} />
                          </div>
                        </th>
                        <th>
                          <div className="th-content">
                            <Briefcase size={16} />
                            <span>Secteur</span>
                          </div>
                        </th>
                        <th>
                          <div className="th-content">
                            <FileText size={16} />
                            <span>Registre</span>
                          </div>
                        </th>
                        <th>
                          <div className="th-content">
                            <CheckCircle size={16} />
                            <span>Statut</span>
                          </div>
                        </th>
                        <th className="th-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.map((client) => (
                        <tr key={client.id}>
                          <td>
                            <div className="client-info">
                              <div className="client-avatar">
                                {client.prenom?.[0]}{client.nom?.[0]}
                              </div>
                              <div className="client-details">
                                <div className="client-name">
                                  {client.prenom} {client.nom}
                                </div>
                                <div className="client-id">ID: {client.id}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="email-cell">
                              {client.email || <span className="text-muted">Non renseigné</span>}
                            </div>
                          </td>
                          <td>
                            <div className="entreprise-cell">
                              {client.entreprise || <span className="text-muted">-</span>}
                            </div>
                          </td>
                          <td>
                            <div className="secteur-cell">
                              {client.secteur || <span className="text-muted">-</span>}
                            </div>
                          </td>
                          <td>
                            <div className="registre-cell">
                              {client.registre_commerce || <span className="text-muted">-</span>}
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${client.is_active ? 'active' : 'inactive'}`}>
                              {client.is_active ? (
                                <>
                                  <UserCheck size={14} />
                                  <span>Actif</span>
                                </>
                              ) : (
                                <>
                                  <UserX size={14} />
                                  <span>Inactif</span>
                                </>
                              )}
                            </span>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button
                                className="btn-action"
                                onClick={() => setShowActionMenu(showActionMenu === client.id ? null : client.id)}
                              >
                                <MoreVertical size={18} />
                              </button>
                              
                              {showActionMenu === client.id && (
                                <div className="action-menu">
                                  <button
                                    className="menu-item"
                                    onClick={() => navigate(`/dashboard-comptable/edit-client/${client.id}`)}
                                  >
                                    <Edit size={16} />
                                    <span>Modifier</span>
                                  </button>
                                  <button
                                    className="menu-item"
                                    onClick={() => toggleClientStatus(client.id, client.is_active)}
                                  >
                                    {client.is_active ? (
                                      <>
                                        <UserX size={16} />
                                        <span>Désactiver</span>
                                      </>
                                    ) : (
                                      <>
                                        <UserCheck size={16} />
                                        <span>Activer</span>
                                      </>
                                    )}
                                  </button>
                                  <button
                                    className="menu-item danger"
                                    onClick={() => deleteClient(client.id)}
                                  >
                                    <Trash2 size={16} />
                                    <span>Supprimer</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .app-container {
          display: flex;
          height: 100vh;
          background: linear-gradient(135deg, #f8fafb 0%, #f1f5f9 100%);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .main-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          margin-left: 320px;
        }

        .main-content {
          flex: 1;
          padding: 32px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(220, 53, 69, 0.3) transparent;
        }

        .main-content::-webkit-scrollbar {
          width: 6px;
        }

        .main-content::-webkit-scrollbar-thumb {
          background: rgba(220, 53, 69, 0.2);
          border-radius: 10px;
        }

        /* Notification */
        .notification {
          position: fixed;
          top: 100px;
          right: 24px;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 24px;
          border-radius: 12px;
          background: white;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          font-weight: 600;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from { transform: translateX(400px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .notification.success {
          border-left: 4px solid #10b981;
          color: #059669;
        }

        .notification.error {
          border-left: 4px solid #dc3545;
          color: #b91c1c;
        }

        /* Header */
        .page-header {
          margin-bottom: 32px;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 24px;
        }

        .page-title {
          font-size: 32px;
          font-weight: 700;
          margin: 0 0 8px 0;
          background: linear-gradient(135deg, #dc3545, #008A44);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          color: #64748b;
          margin: 0;
          font-size: 16px;
          font-weight: 500;
          line-height: 1.5;
        }

        .btn-add {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 28px;
          background: linear-gradient(135deg, #dc3545, #b91c1c);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
          white-space: nowrap;
        }

        .btn-add:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
        }

        /* Filters Section */
        .filters-section {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 300px;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 20px;
          background: white;
          border: 2px solid #e8ecf1;
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .search-box:focus-within {
          border-color: #dc3545;
          box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.1);
        }

        .search-box svg {
          color: #64748b;
        }

        .search-box input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 15px;
          color: #1e293b;
          font-family: inherit;
          background: transparent;
        }

        .search-box input::placeholder {
          color: #94a3b8;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px;
          background: white;
          border: 2px solid #e8ecf1;
          border-radius: 12px;
        }

        .filter-group svg {
          color: #64748b;
        }

        .filter-select {
          border: none;
          outline: none;
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
          cursor: pointer;
          background: transparent;
          font-family: inherit;
        }

        .stats-summary {
          display: flex;
          gap: 20px;
          padding: 12px 24px;
          background: white;
          border-radius: 12px;
          border: 2px solid #e8ecf1;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .stat-label {
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
        }

        .stat-value {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
        }

        .stat-value.active {
          color: #10b981;
        }

        .stat-value.inactive {
          color: #dc3545;
        }

        /* Table Container */
        .table-container {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .clients-table {
          width: 100%;
          border-collapse: collapse;
        }

        .clients-table thead {
          background: linear-gradient(135deg, #f8fafb, #f1f5f9);
        }

        .clients-table th {
          padding: 16px 20px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          cursor: pointer;
          user-select: none;
        }

        .th-content {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .th-content svg {
          flex-shrink: 0;
        }

        .th-content .lucide-arrow-up-down {
          opacity: 0.3;
          transition: opacity 0.2s;
        }

        .th-content .lucide-arrow-up-down.active {
          opacity: 1;
          color: #dc3545;
        }

        .th-actions {
          text-align: center;
          cursor: default;
        }

        .clients-table tbody tr {
          border-bottom: 1px solid #f1f5f9;
          transition: all 0.2s ease;
        }

        .clients-table tbody tr:hover {
          background: #f8fafb;
        }

        .clients-table td {
          padding: 16px 20px;
          font-size: 14px;
          color: #1e293b;
        }

        /* Client Info Cell */
        .client-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .client-avatar {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: linear-gradient(135deg, #dc3545, #008A44);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
          text-transform: uppercase;
          flex-shrink: 0;
        }

        .client-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .client-name {
          font-weight: 600;
          color: #1e293b;
        }

        .client-id {
          font-size: 12px;
          color: #94a3b8;
        }

        .text-muted {
          color: #94a3b8;
          font-style: italic;
        }

        /* Status Badge */
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
        }

        .status-badge.active {
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
          border: 1px solid rgba(16, 185, 129, 0.2);
        }

        .status-badge.inactive {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
          border: 1px solid rgba(220, 53, 69, 0.2);
        }

        /* Actions Cell */
        .actions-cell {
          position: relative;
          display: flex;
          justify-content: center;
        }

        .btn-action {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: transparent;
          border: 2px solid #e8ecf1;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #64748b;
        }

        .btn-action:hover {
          background: #f8fafb;
          border-color: #dc3545;
          color: #dc3545;
        }

        .action-menu {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          border: 1px solid #e8ecf1;
          min-width: 180px;
          z-index: 100;
          overflow: hidden;
          animation: menuSlideIn 0.2s ease;
        }

        @keyframes menuSlideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .menu-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
          font-weight: 500;
          color: #1e293b;
          text-align: left;
        }

        .menu-item:hover {
          background: #f8fafb;
        }

        .menu-item.danger {
          color: #dc3545;
        }

        .menu-item.danger:hover {
          background: rgba(220, 53, 69, 0.1);
        }

        /* Empty State */
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          text-align: center;
        }

        .empty-state svg {
          color: #cbd5e1;
          margin-bottom: 24px;
        }

        .empty-state h3 {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 12px;
        }

        .empty-state p {
          font-size: 15px;
          color: #64748b;
          margin-bottom: 24px;
          max-width: 400px;
        }

        /* Loading State */
        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          text-align: center;
        }

        .spinner-large {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(220, 53, 69, 0.2);
          border-top-color: #dc3545;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 24px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-state p {
          font-size: 16px;
          color: #64748b;
          font-weight: 500;
        }

        /* Responsive */
        @media (max-width: 1023px) {
          .main-wrapper {
            margin-left: 0;
          }

          .main-content {
            padding: 20px;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 16px;
          }

          .page-title {
            font-size: 24px;
          }

          .header-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .btn-add {
            width: 100%;
            justify-content: center;
          }

          .filters-section {
            flex-direction: column;
          }

          .search-box {
            min-width: 100%;
          }

          .stats-summary {
            width: 100%;
            justify-content: space-between;
          }

          .table-wrapper {
            overflow-x: scroll;
          }

          .clients-table {
            min-width: 900px;
          }
        }
      `}</style>
    </>
  );
};

export default ListeClients;